import {useCallback, useId, useMemo, useRef} from 'react'
import {testIdProps} from '@github-ui/test-id-props'
import type {KeyboardEvent} from 'react'
import {Box, merge, type SxProp, FormControl, Checkbox} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'

import {useListViewTitle} from './TitleContext'
import {useListViewSelection} from './SelectionContext'
import {useListViewMultiPageSelection} from './MultiPageSelectionContext'

type SelectAllCheckboxProps = {onToggle?: (value: boolean) => void} & SxProp

const containerStyles: BetterSystemStyleObject = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}
const countStyles: BetterSystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  mb: 0,
  mx: 2,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  fontWeight: 'bold',
  textOverflow: 'ellipsis',
  minWidth: 'min-content',
  flex: 1,
}

export const SelectAllCheckbox = ({onToggle, sx}: SelectAllCheckboxProps) => {
  const {title} = useListViewTitle()
  const {
    isSelectable,
    selectedCount,
    totalCount,
    countOnPage,
    isSelectAllChecked,
    anyItemsSelected,
    singularUnits,
    pluralUnits,
  } = useListViewSelection()
  const {multiPageSelectionAllowed} = useListViewMultiPageSelection()
  const idPrefix = useId()
  const checkboxRef = useRef<HTMLInputElement>(null)
  const notAllItemsSelected = useMemo(() => {
    if (multiPageSelectionAllowed && typeof totalCount === 'number') {
      return selectedCount < Math.max(totalCount, countOnPage)
    }
    return selectedCount < countOnPage
  }, [countOnPage, multiPageSelectionAllowed, selectedCount, totalCount])
  const isIndeterminate = anyItemsSelected && notAllItemsSelected

  const handleBulkSelectToggle = useCallback(() => {
    if (!onToggle) return

    if (isIndeterminate) {
      onToggle(false)
    } else {
      onToggle(!isSelectAllChecked)
    }
  }, [isIndeterminate, isSelectAllChecked, onToggle])

  if (!isSelectable) return null

  const checkboxId = `${idPrefix}-listview-select-all`

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleBulkSelectToggle()
    }
  }

  const units = selectedCount === 1 ? singularUnits : pluralUnits
  const suffix = typeof totalCount === 'number' ? `of ${totalCount} selected` : 'selected'

  return (
    <Box
      sx={sx ? merge<BetterSystemStyleObject>(containerStyles, sx) : containerStyles}
      {...testIdProps('listview-select-all-container')}
      id={`${idPrefix}-listview-select-all-container`}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          minHeight: 32,
          minWidth: 32,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <FormControl id={checkboxId}>
          <Checkbox
            onChange={handleBulkSelectToggle}
            value="default"
            checked={isSelectAllChecked}
            indeterminate={isIndeterminate}
            onKeyDown={onKeyDown}
            tabIndex={0}
            ref={checkboxRef}
            {...testIdProps('select-all-checkbox')}
          />
          <FormControl.Label visuallyHidden {...testIdProps('select-all-label')}>
            Select all {pluralUnits}: {title}
          </FormControl.Label>
        </FormControl>
      </Box>
      <Box
        as="p"
        sx={countStyles}
        className={anyItemsSelected ? undefined : 'sr-only'}
        {...testIdProps('select-all-selected-count')}
      >
        <span aria-hidden="true" {...testIdProps('select-all-selected-count-without-units')}>
          {selectedCount} {suffix}{' '}
        </span>
        <span className={anyItemsSelected ? 'sr-only' : undefined}>
          {selectedCount} {units} {suffix}
        </span>
      </Box>
    </Box>
  )
}

try{ SelectAllCheckbox.displayName ||= 'SelectAllCheckbox' } catch {}